import {Point, Container, IPoint} from 'pixi.js';
import {slotStore} from '@gongxigames/framework/slotV2/globals';

export type TextField = {
    id: string;
    container: Container;
    backPosition: IPoint;
};

export const MOVE_TO_FRONT_DELAY = 200;
export const FADE_DURATION = 200;
export const BG_FADE_DURATION = 60;
export const BG_DELAY = 0;

export class LabelShowManager {

    private readonly updateManager = slotStore.updateManager.value!;

    constructor(
        private frontPosition: Point,
        private textFields: TextField[],
        private bg?: Container,
    ) {}

    show(id: string): void {
        const cont = this.getVisibleCount();

        if (cont) {
            this.showAdditional(id);
        } else {
            this.showOne(id);
        }
    }

    hide(id: string): void {
        const textField = this.getTextField(id);

        this.updateManager
            .addTween(textField.container)
            .to({alpha: 0}, FADE_DURATION)
            .start();

        this.updateManager
            .addTimer(() => {
                this.runAfterHide();
            }, FADE_DURATION + MOVE_TO_FRONT_DELAY, true);
    }

    private runAfterHide(): void {
        const textFields = this.getVisible();
        const textField = textFields[0];

        if (textFields.length === 1) {
            this.updateManager
                .addTween(textField.container)
                .to({...this.frontPosition}, FADE_DURATION)
                .start();

            this.updateManager
                .addTween(textField.container.scale)
                .to({x: 1, y: 1}, FADE_DURATION)
                .start();
        }

        if (textFields.length === 0) {
            this.hideBg();
        }
    }

    private showOne(id: string) {
        const textField = this.getTextField(id);

        textField.container.position.copyFrom(textField.backPosition);

        this.updateManager
            .addTween(textField.container)
            .to({alpha: 1, ...this.frontPosition}, FADE_DURATION)
            .start();

        this.showBg();
    }

    private showAdditional(id: string) {
        const textField = this.getTextField(id);

        this.returnToBackPositions();

        this.updateManager
            .addTween(textField.container)
            .to({alpha: 1, ...textField.backPosition}, FADE_DURATION)
            .start();
    }

    private returnToBackPositions() {
        this.textFields.forEach(item => {
            if (!item.container.alpha) {
                return;
            }

            this.updateManager
                .addTween(item.container)
                .to({...item.backPosition}, FADE_DURATION)
                .start();

            this.updateManager
                .addTween(item.container.scale)
                .to({x: 0.76, y: 0.76}, FADE_DURATION)
                .start();
        });
    }

    private getTextField(id: string): TextField {
        return this.textFields.find(item => item.id === id)!;
    }

    private getVisibleCount(): number {
        return this.textFields.reduce((cont, item) => {
            if (item.container.alpha) {
                cont ++;
            }

            return cont;
        }, 0);
    }

    private getVisible(): TextField[] {
        return this.textFields.filter(item => item.container.alpha);
    }

    private showBg(): void {
        if (this.bg) {
            this.updateManager
                .addTween(this.bg)
                .to({alpha: 1}, BG_FADE_DURATION)
                .start();
        }
    }

    private hideBg(): void {
        if (this.bg) {
            this.updateManager
                .addTween(this.bg)
                .delay(BG_DELAY)
                .to({alpha: 0}, BG_FADE_DURATION)
                .start();
        }
    }
}